import Cookies from 'js-cookie'
import gql from 'graphql-tag'
import { useMutation } from '@apollo/client'
import { useState } from 'react'
import { isEmpty } from 'lodash'
import { Button, Input, WarnMesssage } from '@collegebacker/backer-ui/ui'
import { useHistory } from 'react-router-dom'

import CreateFundHeader from './CreateFundHeader'
import { track } from 'utils/analytics-tracker'
import { Props } from '../createFundInterfaces'
import { getPreviousStep } from '../flowManager'
import { graphQLErrorHandler, isMobile } from 'utils'

import styles from './CreateFundCollectName.module.scss'
import { buildPath } from 'routes'

const userFragment = gql`
  fragment fundCollectionUserFragment on User {
    id
    firstName
    lastName
    photoUrl
  }
`

export const UPDATE_CURRENT_USER = gql`
  mutation UpdateCurrentUser($input: UpdateCurrentUserInputType!) {
    updateCurrentUser(input: $input) {
      ...fundCollectionUserFragment
    }
  }
  ${userFragment}
`

const CURRENT_STEP = 'collect-name'

const CreateFundCollectName = ({
  activeStep,
  updateFundDraft,
  updateFundDraftError,
  loading,
  fundDraft,
  user
}: Props) => {
  const history = useHistory()

  const [firstName, setFirst] = useState(fundDraft.ownerFirstName || '')
  const [lastName, setLast] = useState(fundDraft.ownerLastName || '')
  const [firstNameError, setFirstError] = useState('')
  const [lastNameError, setLastError] = useState('')
  const [updateUserError, setUpdateUserError] = useState('')

  const [updateCurrentUser, { loading: updateUserLoading }] =
    useMutation(UPDATE_CURRENT_USER)

  const submitForm = () => {
    track('OnbCollectName', { step: CURRENT_STEP, firstName, lastName })
    updateFundDraft(CURRENT_STEP, fundDraft, {
      ownerFirstName: firstName,
      ownerLastName: lastName
    })
  }

  if (fundDraft?.fund?.id) {
    history.replace(buildPath('dashboard'))
    return <div />
  }

  return (
    <>
      <CreateFundHeader
        step={activeStep}
        backStep={getPreviousStep(activeStep, user)}
        hideBack={true}
      />
      <section className={styles.container}>
        <form
          className={styles.form}
          onSubmit={(event: any) => {
            event.preventDefault()
            const redirectToCookie = Cookies.get('redirectTo')
            const giftInProgressCookie = Cookies.get('giftInProgress')

            const errors: any = validateName({ firstName, lastName })
            setFirstError(errors['firstName'])
            setLastError(errors['lastName'])

            if (isEmpty(errors)) {
              updateCurrentUser({
                variables: {
                  input: {
                    firstName,
                    lastName
                  }
                }
              })
                .then(() => {
                  if (redirectToCookie && giftInProgressCookie) {
                    Cookies.remove('redirectTo')
                    history.replace(redirectToCookie)
                  } else {
                    submitForm()
                  }
                })
                .catch(graphQLErrorHandler(setUpdateUserError))
            }
          }}
        >
          <div>
            <div className={styles.screenHeaderContainer}>
              <h1 className="typo-app-title-large">What is your name?</h1>
              <p className="typo-app-body-large">
                Let's start with some basic information.
              </p>
              <WarnMesssage
                type="error"
                text={updateFundDraftError || updateUserError}
              />
            </div>
            <Input
              className={styles.input}
              name="First Name"
              label="First Name"
              maxLength={20}
              value={firstName}
              onChange={(e: any) => {
                setFirst(e.target.value)
                setFirstError('')
              }}
              errorMessage={firstNameError}
              autoFocus={!isMobile()}
            />

            <Input
              className={styles.input}
              name="Last Name"
              label="Last Name"
              maxLength={20}
              value={lastName}
              onChange={(e: any) => {
                setLast(e.target.value)
                setLastError('')
              }}
              errorMessage={lastNameError}
            />
          </div>
          <Button
            className={styles.cta}
            label="Continue"
            disabled={loading || updateUserLoading}
            type="submit"
          />
        </form>
      </section>
    </>
  )
}

export function validateName({ firstName, lastName }: any) {
  const errors: any = {}

  if (!firstName || (typeof firstName === 'string' && firstName.trim().length === 0))
    errors['firstName'] = 'First name is required'
  if (!lastName || (typeof lastName === 'string' && lastName.trim().length === 0))
    errors['lastName'] = 'Last name is required'

  return errors
}

export default CreateFundCollectName
